import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { CheckCircle2, ClipboardList } from "lucide-react";
import {
  NOVEMBER_HOUSING_EVENT_ID,
  NOVEMBER_HOUSING_EVENT_PATH,
  UUN_PRIVACY_CONTACT_EMAIL,
  UUN_PRIVACY_NOTICE_PATH,
} from "./config";
import {
  createHousingSurveySubmissionId,
  normaliseHousingDevelopmentSurveyRecord,
  submitHousingDevelopmentSurveyResponse,
  subscribeToHousingDevelopmentSurveys,
} from "./housingDevelopmentService";
import {
  cleanHousingDevelopmentValue,
  createInitialSurveyAnswers,
  normaliseHousingDevelopmentSource,
  normaliseSurveyQuestionType,
  surveySlugFromRecord,
  validateHousingSurveyAnswers,
} from "./housingDevelopmentUtils";
import "./HousingDevelopment.css";

function QuestionError({ id, message }) {
  return message ? <span id={id} className="hd-field-error">{message}</span> : null;
}

function Question({ question, value, error, disabled, onChange, instanceId, index }) {
  const type = normaliseSurveyQuestionType(question.type);
  const fieldId = `${instanceId}-${question.id}`;
  const errorId = `${fieldId}-error`;
  const helpId = `${fieldId}-help`;
  const helper = question.helper || question.helpText || question.help || "";
  const describedBy = [helper ? helpId : "", error ? errorId : ""]
    .filter(Boolean)
    .join(" ") || undefined;
  const options = Array.isArray(question.options)
    ? question.options.map((option) =>
        typeof option === "string" ? option : option?.value || option?.label || ""
      ).filter(Boolean)
    : [];
  const label = (
    <>
      <span className="hd-question__number">{index + 1}.</span>{" "}
      {question.label}
      {question.required ? <span aria-hidden="true"> *</span> : <span className="hd-optional"> (optional)</span>}
    </>
  );

  if (["singleChoice", "yesNo", "multipleChoice", "ratingScale", "rating1To5", "rating1To10", "demographic"].includes(type)) {
    const choices = type === "yesNo"
      ? (options.length ? options : ["Yes", "No"])
      : type === "rating1To5"
      ? ["1", "2", "3", "4", "5"]
      : type === "rating1To10"
      ? ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10"]
      : type === "ratingScale" && !options.length
      ? Array.from(
          { length: Number(question.max || 5) - Number(question.min || 1) + 1 },
          (_, choiceIndex) => String(Number(question.min || 1) + choiceIndex)
        )
      : options;
    const selected = Array.isArray(value) ? value : [];

    return (
      <fieldset
        className="hd-question"
        data-question-id={question.id}
        aria-describedby={describedBy}
      >
        <legend>{label}</legend>
        {helper ? <p id={helpId} className="hd-field-help">{helper}</p> : null}
        <div className="hd-choice-list">
          {choices.map((choice) => {
            const submittedChoice = ["ratingScale", "rating1To5", "rating1To10"].includes(type)
              ? Number(choice)
              : choice;
            const checked = type === "multipleChoice"
              ? selected.includes(choice)
              : value === submittedChoice;
            return (
              <label key={choice} className="hd-choice">
                <input
                  type={type === "multipleChoice" ? "checkbox" : "radio"}
                  name={type === "multipleChoice" ? undefined : fieldId}
                  value={choice}
                  checked={checked}
                  disabled={disabled}
                  onChange={() => {
                    if (type !== "multipleChoice") {
                      onChange(submittedChoice);
                      return;
                    }
                    onChange(
                      checked
                        ? selected.filter((item) => item !== choice)
                        : [...selected, choice]
                    );
                  }}
                />
                <span>{choice}</span>
              </label>
            );
          })}
        </div>
        <QuestionError id={errorId} message={error} />
      </fieldset>
    );
  }

  if (type === "dropdown") {
    return (
      <label className="hd-question" htmlFor={fieldId} data-question-id={question.id}>
        <span className="hd-question__label">{label}</span>
        {helper ? <span id={helpId} className="hd-field-help">{helper}</span> : null}
        <select
          id={fieldId}
          value={value || ""}
          disabled={disabled}
          required={question.required}
          aria-invalid={Boolean(error)}
          aria-describedby={describedBy}
          onChange={(event) => onChange(event.target.value)}
        >
          <option value="">Choose an answer</option>
          {options.map((option) => <option value={option} key={option}>{option}</option>)}
        </select>
        <QuestionError id={errorId} message={error} />
      </label>
    );
  }

  const inputType = type === "email"
    ? "email"
    : type === "phone"
    ? "tel"
    : type === "date"
    ? "date"
    : "text";
  const common = {
    id: fieldId,
    value: value || "",
    disabled,
    required: question.required,
    maxLength: question.maxLength || (type === "longText" ? 2000 : 300),
    autoComplete: question.autoComplete,
    "aria-invalid": Boolean(error),
    "aria-describedby": describedBy,
    onChange: (event) => onChange(event.target.value),
  };

  return (
    <label className="hd-question" htmlFor={fieldId} data-question-id={question.id}>
      <span className="hd-question__label">{label}</span>
      {helper ? <span id={helpId} className="hd-field-help">{helper}</span> : null}
      {type === "longText" ? (
        <textarea {...common} rows="5" />
      ) : (
        <input {...common} type={inputType} />
      )}
      <QuestionError id={errorId} message={error} />
    </label>
  );
}

export default function HousingDevelopmentSurvey({ survey: suppliedSurvey = null, source = "" }) {
  const { surveySlug = "" } = useParams();
  const location = useLocation();
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(!suppliedSurvey);
  const [loadError, setLoadError] = useState("");
  const [attempt, setAttempt] = useState(0);
  const [answers, setAnswers] = useState({});
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [honeypot, setHoneypot] = useState("");
  const loadedSurveyIdRef = useRef("");
  const submissionLockRef = useRef(false);
  const submissionIdRef = useRef("");
  const errorSummaryRef = useRef(null);
  const instanceIdRef = useRef(
    `housing-survey-${Math.random().toString(36).slice(2, 10)}`
  );

  useEffect(() => {
    if (suppliedSurvey) {
      setLoading(false);
      setLoadError("");
      return undefined;
    }
    setLoading(true);
    setLoadError("");
    return subscribeToHousingDevelopmentSurveys(
      (records) => {
        setSurveys(records);
        setLoading(false);
      },
      (error) => {
        console.error("Unable to load Housing & Development survey", error);
        setLoadError("This survey is temporarily unavailable. Please try again.");
        setLoading(false);
      }
    );
  }, [attempt, suppliedSurvey]);

  const currentSurvey = useMemo(() => {
    if (suppliedSurvey) {
      return normaliseHousingDevelopmentSurveyRecord(
        suppliedSurvey.id || suppliedSurvey.surveyId,
        suppliedSurvey
      );
    }
    const requestedSlug = cleanHousingDevelopmentValue(surveySlug).toLowerCase();
    return surveys.find(
      (record) => surveySlugFromRecord(record).toLowerCase() === requestedSlug
    ) || null;
  }, [suppliedSurvey, surveySlug, surveys]);

  useEffect(() => {
    if (!currentSurvey) return;
    const surveyId = cleanHousingDevelopmentValue(
      currentSurvey.id || currentSurvey.surveyId
    );
    if (loadedSurveyIdRef.current === surveyId) return;
    loadedSurveyIdRef.current = surveyId;
    setAnswers(createInitialSurveyAnswers(currentSurvey));
    setErrors({});
    setSubmitError("");
    setSubmitted(false);
    submissionIdRef.current = "";
  }, [currentSurvey]);

  const querySource = new URLSearchParams(location.search).get("source") || "";
  const submissionSource = normaliseHousingDevelopmentSource(source || querySource || "direct");

  const updateAnswer = (questionId, value) => {
    setAnswers((current) => ({ ...current, [questionId]: value }));
    setErrors((current) => {
      if (!current[questionId]) return current;
      const next = { ...current };
      delete next[questionId];
      return next;
    });
    setSubmitError("");
  };

  const focusErrorSummary = () => {
    if (typeof window !== "undefined") {
      window.setTimeout(() => errorSummaryRef.current?.focus(), 0);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!currentSurvey || submissionLockRef.current) return;

    const nextErrors = validateHousingSurveyAnswers(currentSurvey, answers);
    setErrors(nextErrors);
    setSubmitError("");
    if (Object.keys(nextErrors).length > 0) {
      focusErrorSummary();
      return;
    }

    submissionLockRef.current = true;
    setSubmitting(true);
    try {
      if (!submissionIdRef.current) {
        submissionIdRef.current = createHousingSurveySubmissionId();
      }
      const result = await submitHousingDevelopmentSurveyResponse({
        survey: currentSurvey,
        answers,
        source: submissionSource,
        submissionId: submissionIdRef.current,
        honeypot,
      });
      if (
        result?.status !== "submitted" ||
        !cleanHousingDevelopmentValue(result.responseId)
      ) {
        throw new Error("HOUSING_DEVELOPMENT_SURVEY_SUBMISSION_UNCONFIRMED");
      }
      setSubmitted(true);
      setErrors({});
    } catch (error) {
      console.error("Unable to submit Housing & Development survey", error);
      const errorCode = cleanHousingDevelopmentValue(error?.code).toLowerCase();
      setSubmitError(errorCode.endsWith("failed-precondition")
        ? "This survey is no longer accepting responses. Your answers are still here."
        : errorCode.endsWith("invalid-argument")
        ? "The survey changed while you were completing it. Review your answers and try again."
        : "Your responses could not be saved. Check your connection and try again. Your answers are still here.");
      focusErrorSummary();
    } finally {
      submissionLockRef.current = false;
      setSubmitting(false);
    }
  };

  if (loading) {
    return <main className="hd-page hd-survey-page"><p role="status" className="hd-status">Loading survey…</p></main>;
  }
  if (loadError) {
    return (
      <main className="hd-page hd-survey-page">
        <div className="hd-alert" role="alert">
          <p>{loadError}</p>
          <button className="hd-button hd-button--quiet" type="button" onClick={() => setAttempt((value) => value + 1)}>
            Retry
          </button>
        </div>
      </main>
    );
  }
  if (!currentSurvey) {
    return (
      <main className="hd-page hd-survey-page">
        <div className="hd-alert" role="status">
          <h1>Survey unavailable</h1>
          <p>This survey is not currently available.</p>
          <Link className="hd-button hd-button--quiet" to="/housing-development">Housing &amp; Development</Link>
        </div>
      </main>
    );
  }

  if (submitted) {
    const configuredEventPath = cleanHousingDevelopmentValue(
      currentSurvey.eventPath
    );
    const eventPath = configuredEventPath.startsWith("/") &&
      !configuredEventPath.startsWith("//")
      ? configuredEventPath
      : currentSurvey.eventId === NOVEMBER_HOUSING_EVENT_ID
      ? NOVEMBER_HOUSING_EVENT_PATH
      : "";
    return (
      <main className="hd-page hd-survey-page">
        <section className="hd-survey-success" aria-live="polite" aria-labelledby="hd-survey-success-title">
          <CheckCircle2 aria-hidden="true" />
          <h1 id="hd-survey-success-title">Thank you. Your feedback has been submitted.</h1>
          <p>Your response was saved successfully.</p>
          <div className="hd-card__actions">
            {eventPath ? <Link className="hd-button" to={eventPath}>View event</Link> : null}
            <Link className="hd-button hd-button--quiet" to="/housing-development">Housing &amp; Development</Link>
          </div>
        </section>
      </main>
    );
  }

  const questions = Array.isArray(currentSurvey.questions)
    ? currentSurvey.questions
    : [];

  return (
    <main className="hd-page hd-survey-page">
      <header className="hd-survey-header">
        <ClipboardList aria-hidden="true" />
        <p className="hd-kicker">Housing &amp; Development survey</p>
        <h1>{currentSurvey.title || currentSurvey.surveyName}</h1>
        {currentSurvey.introduction ? <p>{currentSurvey.introduction}</p> : null}
        <p className="hd-form-note">
          Fields marked with an asterisk are required. Optional fields are labelled.
        </p>
      </header>

      <form className="hd-survey-form" onSubmit={handleSubmit} noValidate aria-busy={submitting}>
        {Object.keys(errors).length > 0 || submitError ? (
          <div ref={errorSummaryRef} tabIndex="-1" role="alert" className="hd-alert">
            {submitError || "Please answer the highlighted required questions."}
          </div>
        ) : null}

        <fieldset className="hd-survey-fields" disabled={submitting}>
          <legend className="hd-visually-hidden">Survey questions</legend>
          {questions.map((question, index) => (
            <Question
              key={question.id}
              question={question}
              index={index}
              value={answers[question.id]}
              error={errors[question.id]}
              disabled={submitting}
              onChange={(value) => updateAnswer(question.id, value)}
              instanceId={instanceIdRef.current}
            />
          ))}
        </fieldset>

        <div className="hd-honeypot" aria-hidden="true">
          <label htmlFor={`${instanceIdRef.current}-website`}>Leave this field empty</label>
          <input
            id={`${instanceIdRef.current}-website`}
            name="website"
            type="text"
            tabIndex="-1"
            autoComplete="off"
            value={honeypot}
            disabled={submitting}
            onChange={(event) => setHoneypot(event.target.value)}
          />
        </div>

        <section className="hd-data-notice" aria-labelledby="hd-survey-data-title">
          <h2 id="hd-survey-data-title">Your information</h2>
          <p>
            Your answers are collected for this survey and its associated event.
            Individual responses are visible only through authorised UUN admin
            access. This survey closes at the end of 30 November 2026. Identifiable
            responses are kept until 30 November 2027, then deleted or anonymised.
            Please provide only the information requested by the questions.{" "}
            <Link
              className="hd-text-link"
              to={currentSurvey.privacyNoticePath || UUN_PRIVACY_NOTICE_PATH}
            >
              Read the privacy notice
            </Link>
            .
          </p>
        </section>

        <button
          type="submit"
          className="hd-button hd-button--submit hd-button--survey"
          disabled={submitting}
          aria-disabled={submitting}
        >
          {submitting ? "Submitting your feedback…" : "Submit survey"}
        </button>
        <p className="hd-form-contact">
          Need help with this survey? Email{" "}
          <a className="hd-text-link" href={`mailto:${currentSurvey.privacyContactEmail || UUN_PRIVACY_CONTACT_EMAIL}`}>
            {currentSurvey.privacyContactEmail || UUN_PRIVACY_CONTACT_EMAIL}
          </a>.
        </p>
      </form>
    </main>
  );
}

